/**
 * RentalExpensesCalculator.js – modal to estimate annual rental expenses (management, maintenance, rates, insurance, etc.).
 * Used in Client Profile Step 4 (Family Home). Props: onClose, onSave, propertyValue, weeklyRent, currentExpenses.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, X } from 'lucide-react';
import { CurrencyInput } from './ui/index.tsx';
import { formatCurrency } from '../utils/index.ts';

interface RentalExpenses {
    managementFees: number;
    maintenance: number;
    landTax: number;
    councilRates: number;
    insurance: number;
    strata: number;
    other: number;
}

interface RentalExpensesCalculatorProps {
    onClose: () => void;
    onSave: (total: number) => void;
    propertyValue: number;
    weeklyRent: number;
    currentExpenses: number;
}

const RentalExpensesCalculator = ({ onClose, onSave, propertyValue, weeklyRent, currentExpenses }: RentalExpensesCalculatorProps) => {
    const [propertyType, setPropertyType] = useState<'house' | 'apartment'>('house');
    const [expenses, setExpenses] = useState<RentalExpenses>({
        managementFees: 0,
        maintenance: 0,
        landTax: 0,
        councilRates: 0,
        insurance: 0,
        strata: 0,
        other: 0
    });

    useEffect(() => {
        const annualRent = weeklyRent * 52;
        const managementFees = annualRent * 0.08;
        let maintenanceRate;
        if (propertyValue <= 1000000) {
            maintenanceRate = 0.015;
        } else if (propertyValue <= 2000000) {
            maintenanceRate = 0.01;
        } else {
            maintenanceRate = 0.0075;
        }
        const maintenance = propertyValue * maintenanceRate;
        setExpenses(prev => ({
            ...prev,
            managementFees: managementFees || 0,
            maintenance: maintenance || 0
        }));
    }, [weeklyRent, propertyValue]);

    useEffect(() => {
        if (currentExpenses > 0 && expenses.managementFees === 0 && expenses.maintenance === 0) {
            // User can manually adjust if needed
        }
    }, [currentExpenses]);

    const handleUpdate = (field: string, value: number | undefined) => {
        setExpenses(prev => ({
            ...prev,
            [field]: value ?? 0
        }));
    };

    const total = useMemo(() => Object.values(expenses).reduce((sum, val) => sum + val, 0), [expenses]);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Calculator /> Rental Expenses Calculator
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-all duration-300 hover:scale-110">
                        <X />
                    </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    Calculate your estimated annual expenses if you were to rent out your property. Some values are auto-calculated based on your property value and rent amount.
                </p>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={() => setPropertyType('house')}
                            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                                propertyType === 'house' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            House
                        </button>
                        <button
                            type="button"
                            onClick={() => setPropertyType('apartment')}
                            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                                propertyType === 'apartment' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Apartment
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-2 space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-blue-900 mb-3">Auto-Calculated (based on your inputs)</p>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 items-center gap-4">
                                <div>
                                    <label className="font-medium text-gray-700">Property Management Fees</label>
                                    <p className="text-xs text-gray-500">8% of annual rent</p>
                                </div>
                                <div>
                                    <CurrencyInput name="managementFees" value={expenses.managementFees || ''} onChange={(v) => handleUpdate('managementFees', v)} />
                                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(expenses.managementFees)}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 items-center gap-4">
                                <div>
                                    <label className="font-medium text-gray-700">Repairs & Maintenance</label>
                                    <p className="text-xs text-gray-500">
                                        {propertyValue <= 1000000 ? '1.5%' : propertyValue <= 2000000 ? '1%' : '0.75%'} of property value
                                    </p>
                                </div>
                                <div>
                                    <CurrencyInput name="maintenance" value={expenses.maintenance || ''} onChange={(v) => handleUpdate('maintenance', v)} />
                                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(expenses.maintenance)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {propertyType === 'house' && (
                            <div className="grid grid-cols-2 items-center gap-4">
                                <label className="font-medium text-gray-700">Land Tax ($)</label>
                                <CurrencyInput name="landTax" value={expenses.landTax || ''} onChange={(v) => handleUpdate('landTax', v)} />
                            </div>
                        )}
                        <div className="grid grid-cols-2 items-center gap-4">
                            <label className="font-medium text-gray-700">Council Rates ($)</label>
                            <CurrencyInput name="councilRates" value={expenses.councilRates || ''} onChange={(v) => handleUpdate('councilRates', v)} />
                        </div>
                        <div className="grid grid-cols-2 items-center gap-4">
                            <label className="font-medium text-gray-700">Landlord's Insurance ($)</label>
                            <CurrencyInput name="insurance" value={expenses.insurance || ''} onChange={(v) => handleUpdate('insurance', v)} />
                        </div>
                        {propertyType === 'apartment' && (
                            <div className="grid grid-cols-2 items-center gap-4">
                                <label className="font-medium text-gray-700">Strata / Body Corporate ($)</label>
                                <CurrencyInput name="strata" value={expenses.strata || ''} onChange={(v) => handleUpdate('strata', v)} />
                            </div>
                        )}
                        <div className="grid grid-cols-2 items-center gap-4">
                            <label className="font-medium text-gray-700">Other Expenses ($)</label>
                            <CurrencyInput name="other" value={expenses.other || ''} onChange={(v) => handleUpdate('other', v)} />
                        </div>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t flex justify-between items-center">
                    <p className="text-xl font-bold">Total Annual Expenses: {formatCurrency(total)}</p>
                    <button
                        onClick={() => onSave(total)}
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-teal-400 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                    >
                        Save Expenses
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RentalExpensesCalculator;
